import { useCallback, useRef, useState } from "react";

const MAX_HISTORY = 100;

// Undo/redo history with separate live (no-history) and committed updates.
export function useHistory(elements, setElements) {
  const historyRef = useRef({ past: [], future: [] });
  const [, bump] = useState(0);
  const force = useCallback(() => bump((v) => v + 1), []);

  const push = (prev) => {
    historyRef.current.past.push(prev);
    if (historyRef.current.past.length > MAX_HISTORY) historyRef.current.past.shift();
    historyRef.current.future = [];
  };

  // Commit a change AND record it in history.
  const commit = useCallback(
    (updater) => {
      setElements((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        push(prev);
        return next;
      });
      force();
    },
    [setElements, force]
  );

  // Record current state as an undo checkpoint without changing it.
  const checkpoint = useCallback(() => {
    setElements((prev) => {
      push(prev);
      return prev;
    });
    force();
  }, [setElements, force]);

  // Update without touching history (used during drags / live scrubbing).
  const setLive = useCallback((updater) => setElements(updater), [setElements]);

  const undo = useCallback(() => {
    const h = historyRef.current;
    if (!h.past.length) return;
    setElements((prev) => {
      h.future.push(prev);
      return h.past.pop();
    });
    force();
  }, [setElements, force]);

  const redo = useCallback(() => {
    const h = historyRef.current;
    if (!h.future.length) return;
    setElements((prev) => {
      h.past.push(prev);
      return h.future.pop();
    });
    force();
  }, [setElements, force]);

  return {
    commit,
    checkpoint,
    setLive,
    undo,
    redo,
    canUndo: historyRef.current.past.length > 0,
    canRedo: historyRef.current.future.length > 0,
  };
}
